import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MUICheckbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { FormHelperText } from '@material-ui/core';

import { ElemType, MapClassesToElem } from 'core/helpers/styles-helpers';
import { Tooltip } from 'core/components/tooltip';
import { Label } from 'core/components/label';
import useStyles from './checkbox.styles';

function Checkbox({
  id,
  label,
  value,
  onChange,
  style,
  mode,
  disabled,
  error,
  helperText,
  // #33448: the error helper text is hidden by default (legacy `helperText: { display: 'none' }`).
  // Opt in with showHelperText to surface required-checkbox validation errors (e.g. the /verifyFarmUW
  // attestation) without changing every other checkbox that relied on the hidden default.
  showHelperText,
  className,
  topAlignCheckbox,
  tooltipText,
  tooltipLabel,
  checkboxTooltipClassName: tooltipClassName,
  inlineDisplay,
  labelClassName,
  subLabel,
  description,
  descriptionClassName,
  ...props
}) {
  const classes = useStyles();

  const getCheckboxClass = useCallback(() => {
    if (error) {
      if (topAlignCheckbox) {
        return { root: classes.checkboxLongLabelError };
      }
      return { root: classes.error };
    }
    if (topAlignCheckbox && mode) {
      if (mode === 'light') {
        return { root: classes.lightCheckboxLongLabel };
      }
      return { root: classes.darkCheckboxLongLabel };
    }
    if (mode) {
      return MapClassesToElem(`${mode}-${ElemType.Checkbox}`, classes);
    }
  }, [error, topAlignCheckbox, classes, mode]);

  const controlClasses = error
    ? { label: classes.error, root: classes.customControlLabel }
    : {
        label: (mode && classes[`${mode}-label`]) || classes.label,
        root: classes.customControlLabel
      };

  return (
    <FormControl className={classNames(classes.container, className)} disabled={disabled} style={style} {...props}>
      <FormControlLabel
        classes={controlClasses}
        label={
          <Label className={classNames(classes.labelContainer, labelClassName)} inlineDisplay={inlineDisplay} id={id}>
            {label}
            {description && (
              <Label
                type={'formFieldDescription'}
                style={{ whiteSpace: 'pre-wrap' }}
                className={descriptionClassName || classes.description}
              >
                {description}
              </Label>
            )}
            {tooltipText && (
              <Tooltip
                text={tooltipText}
                placement="bottom"
                label={tooltipLabel || 'More Info'}
                className={tooltipClassName}
              />
            )}
            {subLabel ? <Label className={classes.subLabel}>{subLabel}</Label> : null}
          </Label>
        }
        control={
          <MUICheckbox
            disabled={disabled}
            id={id}
            checked={value}
            onChange={onChange}
            value={id}
            color="primary"
            classes={getCheckboxClass()}
          />
        }
      />
      {error && (
        <FormHelperText
          classes={{ root: showHelperText ? classes.visibleHelperText : classes.helperText }}
          error={error}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

Checkbox.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  style: PropTypes.object,
  mode: PropTypes.oneOf([null, 'light', 'dark']),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  showHelperText: PropTypes.bool,
  className: PropTypes.string,
  topAlignCheckbox: PropTypes.bool,
  tooltipText: PropTypes.string,
  tooltipLabel: PropTypes.string,
  checkboxTooltipClassName: PropTypes.string,
  inlineDisplay: PropTypes.bool,
  labelClassName: PropTypes.string
};

Checkbox.defaultProps = {
  value: undefined,
  style: {},
  mode: null,
  disabled: false,
  error: false,
  helperText: undefined,
  showHelperText: false,
  className: '',
  labelClassName: '',
  topAlignCheckbox: false,
  tooltipText: '',
  tooltipLabel: '',
  checkboxTooltipClassName: null,
  inlineDisplay: false
};

export default Checkbox;
